// Client-side SignalR service with ultra-lazy loading

import { buildConfigVariables } from "@/config/general/utils";
import { Message, PlayerType, ScoreMessage } from "@/interFace/inbox/types";
import { frontendLogger } from "../logger";

// Ultra-lazy SignalR loading - only when actually used
let signalRPromise: Promise<any> | null = null;

function loadSignalR() {
  if (!signalRPromise) {
    signalRPromise = (async () => {
      try {
        // Use standard dynamic import so Next.js bundles it into a separate chunk
        const signalR = await import(/* webpackChunkName: "signalr" */ "@microsoft/signalr");
        return signalR;
      } catch (error) {
        console.error("Failed to load SignalR:", error);
        throw error;
      }
    })();
  }
  return signalRPromise;
}

export async function sendSignalRMessage(
  connection: any,
  from: string,
  to: string,
  text: string,
  toUserStatus: number
) {
  try {
    //const uid = await GetUserId();

    if (connection === undefined || connection == null) {
      connection = await createConnection(connection);
    }

    connection = await startConnection(connection);

    if (text.trim()) {
      // const groupNameSender = uid;
      await connection.invoke("JoinGroup", to);
      await connection.invoke("PostMessage", from, to, text, toUserStatus);

      //console.log('SOCKET: Message sent to ', to);
    }
  } catch (error) {
    console.error("SOCKET Error: ", error);
  }
}

export async function postStartBattle(connection: any, raceId: string) {
  try {
    //const uid = await GetUserId();

    if (connection === undefined || connection == null) {
      connection = await createConnection(connection);
    }

    connection = await startConnection(connection);

    await connection.invoke("PostStartBattle", raceId);
  } catch (error) {
    console.error("SOCKET Error: ", error);
  }
}

export async function JoinRaceGroup(connection: any | null, raceId: string) {
  const groupName = raceId;

  try {
    //const uid = await GetUserId();

    if (connection === undefined || connection == null) {
      connection = await createConnection(connection);
    }

    connection = await startConnection(connection);

    if (connection && typeof connection.invoke === 'function') {  
      const response = await connection.invoke("JoinRaceGroup", groupName);
      return response;
    }
    else {
      console.log("SignalR connection is not valid for invoking JoinRaceGroup.");
    }
  } catch (error) {
    console.error("SOCKET Error: ", error);
  }
}

export async function sendSignalRAnswer(
  connection: any,
  from: string,
  raceId: string,
  isCorrect: boolean
) {
  const groupName = raceId;

  try {
    //const uid = await GetUserId();

    if (connection === undefined || connection == null) {
      connection = await createConnection(connection);
    }
    connection = await startConnection(connection);

    await connection.invoke("JoinGroup", groupName);
    await connection.invoke("PostScore", from, groupName, isCorrect);
  } catch (error) {
    console.error("SOCKET Error: ", error);
  }
}

async function createConnection(connection: any | null) {
  const configVariables = await buildConfigVariables();
  const BACKEND_URL = configVariables.NEXT_PUBLIC_BACKEND_URL || "";
  const url = `${BACKEND_URL}/hub`;

  if (connection === null || connection === undefined) {
    const signalR = await loadSignalR();
    const { HubConnectionBuilder, LogLevel } = signalR;

    const newConnection = new HubConnectionBuilder()
      .withUrl(url, {
        logger: LogLevel.Error,
        withCredentials: false,
        accessTokenFactory: async () => {
          try {
            const { getFirebaseAuth } = await import("@/config/firebase/firebaseClient");
            const auth = await getFirebaseAuth();
            const currentUser = auth?.currentUser;

            if (!currentUser) {
              return "";
            }

            return await currentUser.getIdToken();
          } catch (error) {
            console.error("Failed to get Firebase token for SignalR:", error);
            return "";
          }
        },
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext: import("@microsoft/signalr").RetryContext) => {
          // Exponential backoff: 0s, 2s, 10s, 30s
          if (retryContext.previousRetryCount === 0) return 0;
          if (retryContext.previousRetryCount === 1) return 2000;
          if (retryContext.previousRetryCount === 2) return 10000;
          return 30000;
        }
      })
      .build();

    return newConnection;
  } else {
    return connection;
  }
}

async function startConnection(connection: any) {
  try {
    if (connection === undefined || connection == null) {
      connection = await createConnection(connection);
    }

    // If already connected, return immediately
    if (connection.state === "Connected") return connection;

    // Only call start() when the connection is in the Disconnected state.
    // Calling start() when connection is Connecting/Reconnecting/Connected will throw.
    if (connection.state === "Disconnected") {
      await connection.start();
      return connection;
    }

    // If connection is in Connecting or Reconnecting state, wait briefly until it becomes Connected
    const waitForConnected = async (timeout = 5000) =>
      new Promise<void>((resolve) => {
        const start = Date.now();
        const interval = setInterval(() => {
          if (connection.state === "Connected") {
            clearInterval(interval);
            resolve();
          } else if (Date.now() - start > timeout) {
            clearInterval(interval);
            // If it never connected within timeout, attempt to start if disconnected
            if (connection.state === "Disconnected") {
              connection.start().then(() => resolve()).catch(() => resolve());
            } else {
              resolve();
            }
          }
        }, 100);
      });

    await waitForConnected();

    if (connection.state === "Connected") return connection;

    // If after waiting it's Disconnected, try once more to start
    if (connection.state === "Disconnected") {
      await connection.start();
      if (connection.state === "Connected") return connection;
    }

    throw new Error(`Failed to establish SignalR connection. Current state: ${connection.state}`);
  } catch (err) {
    throw err;
  }
}

function stopConnection(connection: any) {
  try {
    //console.log('SOCKET: Stoping connection ', connection);
    connection.stop();
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : String(err);
    console.log(errorMessage);
  }
}

async function registerOnServerEvents(
  callback: (payload: Message) => void,
  callbackScore: (payload: ScoreMessage) => void,
  callbackCommand: (payload: string) => void
) {
  try {
    const connection = await createConnection(null);
    // connection.on("TextMessage", (payload: Message) => {
    //   callback(payload);
    // });
    connection.on("PushNotification", (payload: Message) => {
      callback(payload);
    });
    connection.on("BattleMessage", (payload: ScoreMessage) => {
      callbackScore(payload);
    });
    connection.on("ExecuteCommand", (payload: string) => {
      callbackCommand(payload);
    });

    await startConnection(connection);

    return connection;
  } catch (error) {
    console.error("SOCKET: Error registering server events", error);
    return null;
  }
}

export const socketService = {
  registerOnServerEvents,
  stopConnection,
};
